import { CommonModule, NgClass } from '@angular/common'
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core'
import { ValidatorFn, Validators } from '@angular/forms'
import { CKEditorModule, type ChangeEvent } from '@ckeditor/ckeditor5-angular'
import {
  ClassicEditor,
  Essentials,
  Bold,
  Italic,
  Link,
  List,
  Paragraph
} from 'ckeditor5'
import { CaseInput } from '../../../interfaces/case-input.interface'
import { HtmlTooltipDirective } from '../../../standalone-exports'

@Component({
    selector: 'case-rich-text-input',
    templateUrl: './rich-text-input.component.html',
    styleUrls: ['./rich-text-input.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      HtmlTooltipDirective,
      CKEditorModule,
      NgClass
    ],
})
export class RichTextInputComponent implements CaseInput, OnChanges {
  @Input() label: string
  @Input() initialValue: string
  @Input() placeholder: string
  @Input() helpText: string
  @Input() htmlHelpText?: string
  @Input() htmlTooltip?: string
  @Input() htmlExample?: string
  @Input() showErrors = false
  @Input() validators: ValidatorFn[] = []
  @Input() uniqueId: string
  @Input() max?: number
  @Input() renderLabelAsHtml = false
  @Input() editorMinHeight = 400

  @Output() valueChanged: EventEmitter<string> = new EventEmitter()

  public Editor = ClassicEditor
  public editorConfig: any

  content: string = ''
  required: boolean
  showExampleModal = false

  ngOnChanges(changes: SimpleChanges) {
    if (Object.keys(changes).length === 1 && changes['showErrors']) {
      return
    }

    if (changes['initialValue'] && this.content !== this.initialValue) {
      this.content = this.initialValue || ''
    }

    this.required = this.validators.includes(Validators.required)

    this.editorConfig = {
      // Acknowledge the GPL license for the free version to use the editor
      licenseKey: 'GPL',

      plugins: [Essentials, Bold, Italic, Link, List, Paragraph],
      toolbar: ['bold', 'italic', 'link', '|', 'bulletedList'],
      link: {
        addTargetToExternalLinks: true
      },
      placeholder: this.placeholder
    }

    if (this.max) {
      const hasMaxLength = this.validators.some(
        (v) => v === Validators.maxLength(this.max)
      )
      if (!hasMaxLength) {
        this.validators.push(Validators.maxLength(this.max))
      }
    }
  }

  onChange(event: ChangeEvent) {
    if (!event || !event.editor) {
      return
    }

    try {
      const newContent = event.editor.getData() || ''
      setTimeout(() => {
        this.content = newContent
        this.valueChanged.emit(newContent)
      }, 0)
    } catch (error) {
      console.warn('Error in rich-text-input onChange:', error)
    }
  }

  @HostListener('document:click', ['$event.target'])
  clickOut(eventTarget) {
    if (
      this.showExampleModal &&
      eventTarget.className.includes('modal-background')
    ) {
      this.showExampleModal = false
    }
  }
}
